import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  ClipboardList, 
  UserPlus,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SendDigestButton from '../components/SendDigestButton';
import { toast } from 'sonner';
import { trackModuleVisit } from '@/lib/posthog';

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('super_admin');
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`),
        axios.get(`${API}/super-admin/building-requests?status=pending`)
      ]);
      setStats(statsRes.data);
      setPendingRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching super admin data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    { 
      label: 'Conjuntos Activos', 
      value: stats?.total_buildings || 0, 
      icon: Building2, 
      color: 'bg-blue-500',
      link: '/super-admin/conjuntos'
    },
    { 
      label: 'Usuarios Totales', 
      value: stats?.total_users || 0, 
      icon: Users, 
      color: 'bg-green-500',
      link: '/super-admin/usuarios'
    },
    { 
      label: 'Administradores', 
      value: stats?.total_admins || 0, 
      icon: UserPlus, 
      color: 'bg-purple-500',
      link: '/super-admin/usuarios?role=admin'
    },
    { 
      label: 'Solicitudes Pendientes', 
      value: stats?.pending_requests || 0, 
      icon: ClipboardList, 
      color: 'bg-orange-500',
      link: '/super-admin/solicitudes'
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="super-admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Panel Super Admin</h1>
            <p className="text-slate-500 mt-1">Bienvenido, {user?.name}. Gestiona toda la plataforma desde aquí.</p>
          </div>
          <SendDigestButton />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-xl text-white`}>
                      <stat.icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Activity Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-500" />
                Actividad Reciente (7 días)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-xl">
                  <p className="text-sm text-green-600 font-medium">Nuevos Usuarios</p>
                  <p className="text-2xl font-bold text-green-700">{stats?.new_users_7d || 0}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl">
                  <p className="text-sm text-blue-600 font-medium">Nuevos Conjuntos</p>
                  <p className="text-2xl font-bold text-blue-700">{stats?.new_buildings_7d || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/super-admin/solicitudes">
                <Button variant="outline" className="w-full justify-start gap-2 hover:bg-orange-50 hover:border-orange-300">
                  <ClipboardList className="h-4 w-4 text-orange-500" />
                  Revisar Solicitudes
                  {stats?.pending_requests > 0 && (
                    <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {stats.pending_requests}
                    </span>
                  )}
                </Button>
              </Link>
              <Link to="/super-admin/invitar-admin">
                <Button variant="outline" className="w-full justify-start gap-2 hover:bg-blue-50 hover:border-blue-300">
                  <UserPlus className="h-4 w-4 text-blue-500" />
                  Invitar Administrador
                </Button>
              </Link>
              <Link to="/super-admin/conjuntos">
                <Button variant="outline" className="w-full justify-start gap-2 hover:bg-green-50 hover:border-green-300">
                  <Building2 className="h-4 w-4 text-green-500" />
                  Ver Conjuntos
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Pending Requests Preview */}
        {pendingRequests.length > 0 && (
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertCircle className="h-5 w-5" />
                Solicitudes Pendientes de Aprobación
              </CardTitle>
              <CardDescription className="text-orange-600">
                Hay {pendingRequests.length} solicitud(es) esperando tu revisión
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingRequests.slice(0, 3).map((request) => (
                  <div 
                    key={request.id} 
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-orange-200"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{request.name}</p>
                      <p className="text-sm text-slate-500">
                        Solicitado por: {request.requested_by_name}
                      </p>
                      <p className="text-xs text-slate-400">{request.city}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-orange-600">Pendiente</span>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/super-admin/solicitudes">
                <Button className="w-full mt-4 bg-orange-500 hover:bg-orange-600">
                  Ver Todas las Solicitudes
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
